import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Building2, Stethoscope, Sparkles, Briefcase, Users,
  Clock, TrendingDown, EyeOff, UserX,
  Megaphone, FileText, MessageSquare, Database, CalendarCheck,
  ArrowUpRight, CheckCircle2,
} from "lucide-react";

const examples = [
  { icon: Stethoscope, name: "Dentists & Medical Clinics" },
  { icon: Sparkles, name: "Cosmetic Clinics" },
  { icon: Briefcase, name: "Immigration Consultants" },
  { icon: Users, name: "Other High-Ticket Service Providers" },
];

const problems = [
  { icon: Clock, text: "Leads are not followed up quickly" },
  { icon: TrendingDown, text: "Low booking rates despite ad spend" },
  { icon: EyeOff, text: "No clear visibility into lead quality" },
  { icon: UserX, text: "Missed opportunities due to manual processes" },
];

const solutionSteps = [
  { icon: Megaphone, text: "Ads to generate demand" },
  { icon: FileText, text: "Landing pages to capture leads" },
  { icon: MessageSquare, text: "Instant follow-ups via SMS/WhatsApp" },
  { icon: Database, text: "CRM systems to track and prioritize leads" },
  { icon: CalendarCheck, text: "Booking systems to convert interest into appointments" },
];

const caseStudies = [
  {
    clientType: "Dental Clinic",
    problem: "Spending on Google Ads but only 15% of leads were converting to appointments. No follow-up system in place.",
    solution: "Implemented instant SMS follow-ups, a conversion-optimized landing page, and a CRM pipeline to track every lead.",
    results: ["120+ leads/month generated", "35% increase in booking rate", "Lead response time reduced to under 60 seconds"],
  },
  {
    clientType: "Immigration Consultancy",
    problem: "High lead volume but poor qualification. Sales team wasted time on unqualified prospects.",
    solution: "Built a structured intake form, automated qualification flow, and priority-based CRM routing.",
    results: ["40% reduction in unqualified consultations", "2x improvement in consultation-to-client conversion", "Automated follow-up sequence for nurturing"],
  },
];

export const ServiceBusinessesSection = () => {
  return (
    <section className="py-20 px-4 bg-secondary">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          className="mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Service Businesses</h2>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Dentists, clinics, and consultants who rely on consistent bookings to grow.
          </p>
        </motion.div>

        {/* Examples */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          {examples.map((ex, i) => (
            <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
              <ex.icon className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="text-sm font-medium text-foreground">{ex.name}</span>
            </div>
          ))}
        </motion.div>

        {/* Two columns: Problems + Solution */}
        <div className="grid md:grid-cols-2 gap-8 mb-14">
          {/* Problems */}
          <motion.div
            className="p-6 md:p-8 rounded-2xl bg-card border border-border"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold text-foreground mb-6">Common Problems</h3>
            <div className="space-y-4">
              {problems.map((p, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <p.icon className="w-4 h-4 text-destructive" />
                  </div>
                  <p className="text-muted-foreground pt-1.5">{p.text}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Solution */}
          <motion.div
            className="p-6 md:p-8 rounded-2xl bg-card border border-border"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold text-foreground mb-3">Our Solution</h3>
            <p className="text-muted-foreground text-sm mb-5">We build a complete lead-to-booking system:</p>
            <div className="space-y-3">
              {solutionSteps.map((s, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                  <s.icon className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground">{s.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Case Studies */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-foreground mb-6">Real Results</h3>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {caseStudies.map((cs, i) => (
              <div key={i} className="p-6 rounded-2xl bg-card border border-border hover:shadow-lg transition-shadow">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                  {cs.clientType}
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Problem</p>
                    <p className="text-sm text-foreground">{cs.problem}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Solution</p>
                    <p className="text-sm text-foreground">{cs.solution}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Results</p>
                    <div className="space-y-2">
                      {cs.results.map((r, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-sm font-medium text-foreground">{r}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Link to="/demo">
            <Button size="lg" className="text-base px-8 py-6">
              See How This Works for Your Business
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
